import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle2, FileText, Plus, RefreshCw, Settings, XCircle, Download, Send } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
    queued:     { label: "Na fila",     variant: "secondary" },
    processing: { label: "Processando", variant: "secondary" },
    issued:     { label: "Emitida",     variant: "default" },
    error:      { label: "Erro",        variant: "destructive" },
    cancelled:  { label: "Cancelada",   variant: "outline" },
  };
  const s = map[status] ?? { label: status, variant: "secondary" as const };
  return <Badge variant={s.variant}>{s.label}</Badge>;
}

// ─── Formulário de emissão ────────────────────────────────────────────────────
function EmitirNfseDialog({ onSuccess }: { onSuccess: () => void }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    tomadorNome: "",
    tomadorCpf: "",
    tomadorEmail: "",
    descricaoServico: "Serviços de beleza e estética",
    valorTotal: "",
    aliquotaIss: "",
  });

  const emitir = trpc.fiscal.emitir.useMutation({
    onSuccess: (data) => {
      toast.success("✅ Nota fiscal enviada!", { description: data.message });
      setOpen(false);
      setForm({ tomadorNome: "", tomadorCpf: "", tomadorEmail: "", descricaoServico: "Serviços de beleza e estética", valorTotal: "", aliquotaIss: "" });
      onSuccess();
    },
    onError: (err) => {
      toast.error("Erro ao emitir nota", { description: err.message });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const valor = parseFloat(form.valorTotal.replace(",", "."));
    if (isNaN(valor) || valor <= 0) {
      toast.error("Valor inválido", { description: "Informe um valor maior que zero." });
      return;
    }
    emitir.mutate({
      tomadorNome: form.tomadorNome,
      tomadorCpf: form.tomadorCpf.replace(/\D/g, "") || undefined,
      tomadorEmail: form.tomadorEmail || undefined,
      descricaoServico: form.descricaoServico,
      valorTotal: valor,
      aliquotaIss: form.aliquotaIss ? parseFloat(form.aliquotaIss) : undefined,
    });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Emitir NFS-e
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Emitir Nota Fiscal de Serviço</DialogTitle>
          <DialogDescription>
            Preencha os dados do tomador e do serviço para emitir a NFS-e via Notaas.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nome do tomador *</Label>
            <Input
              placeholder="Nome completo ou razão social"
              value={form.tomadorNome}
              onChange={(e) => setForm({ ...form, tomadorNome: e.target.value })}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>CPF do tomador</Label>
              <Input
                placeholder="000.000.000-00"
                value={form.tomadorCpf}
                onChange={(e) => setForm({ ...form, tomadorCpf: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>E-mail do tomador</Label>
              <Input
                type="email"
                placeholder="email@exemplo.com"
                value={form.tomadorEmail}
                onChange={(e) => setForm({ ...form, tomadorEmail: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descrição do serviço *</Label>
            <Textarea
              placeholder="Descreva o serviço prestado"
              value={form.descricaoServico}
              onChange={(e) => setForm({ ...form, descricaoServico: e.target.value })}
              required
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Valor total (R$) *</Label>
              <Input
                placeholder="0,00"
                value={form.valorTotal}
                onChange={(e) => setForm({ ...form, valorTotal: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Alíquota ISS (%)</Label>
              <Input
                placeholder="2.00 (padrão da config)"
                value={form.aliquotaIss}
                onChange={(e) => setForm({ ...form, aliquotaIss: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={emitir.isPending} className="gap-2">
              {emitir.isPending ? (
                <><RefreshCw className="h-4 w-4 animate-spin" /> Enviando...</>
              ) : (
                <><Send className="h-4 w-4" /> Emitir Nota</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Aba de Configuração ──────────────────────────────────────────────────────
function ConfigTab() {
  const { data: config, refetch } = trpc.fiscal.getConfig.useQuery();
  const [form, setForm] = useState<Record<string, string | boolean>>({});
  const [dirty, setDirty] = useState(false);

  const save = trpc.fiscal.saveConfig.useMutation({
    onSuccess: (data) => {
      if (data.isConfigured) {
        toast.success("✅ Configuração salva!", { description: "Seu salão está pronto para emitir notas fiscais." });
      } else {
        toast("Configuração salva", { description: "Falta preencher CNPJ e API Key para ativar a emissão." });
      }
      setDirty(false);
      refetch();
    },
    onError: (err) => toast.error("Erro ao salvar", { description: err.message }),
  });

  const testConn = trpc.fiscal.testConnection.useMutation({
    onSuccess: (data) => toast.success("✅ Conexão OK", { description: data.message }),
    onError: (err) => toast.error("Falha na conexão", { description: err.message }),
  });

  function field(key: string) {
    const val = form[key] !== undefined ? form[key] : (config as Record<string, unknown>)?.[key] ?? "";
    return {
      value: String(val),
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
        setDirty(true);
      },
    };
  }

  function handleSave() {
    const payload: Record<string, unknown> = {};
    const keys = ["cnpj","razaoSocial","nomeFantasia","inscricaoMunicipal","regimeTributario",
      "logradouro","numero","complemento","bairro","cidade","uf","cep",
      "aliquotaIss","codigoServico","descricaoServico","notaasApiKey","ambiente"];
    for (const k of keys) {
      const v = form[k] !== undefined ? form[k] : (config as Record<string, unknown>)?.[k];
      if (v !== undefined && v !== null) payload[k] = v;
    }
    if (form.issRetido !== undefined) payload.issRetido = form.issRetido;
    save.mutate(payload as Parameters<typeof save.mutate>[0]);
  }

  if (!config) return <div className="text-muted-foreground py-8 text-center">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* Status */}
      <div className={`flex items-center gap-3 rounded-lg border p-4 ${config.isConfigured ? "border-green-500/30 bg-green-500/5" : "border-yellow-500/30 bg-yellow-500/5"}`}>
        {config.isConfigured ? (
          <CheckCircle2 className="h-5 w-5 text-green-500 shrink-0" />
        ) : (
          <AlertCircle className="h-5 w-5 text-yellow-500 shrink-0" />
        )}
        <div>
          <p className="font-medium text-sm">
            {config.isConfigured ? "Emissão de notas ativa" : "Configuração incompleta"}
          </p>
          <p className="text-xs text-muted-foreground">
            {config.isConfigured
              ? "CNPJ e API Key configurados. Pronto para emitir NFS-e."
              : "Preencha o CNPJ e a API Key do Notaas para ativar a emissão."}
          </p>
        </div>
      </div>

      {/* Dados da empresa */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Dados do Emissor</CardTitle>
          <CardDescription>Informações fiscais do seu salão</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CNPJ *</Label>
              <Input placeholder="00000000000000" maxLength={14} {...field("cnpj")} />
            </div>
            <div className="space-y-2">
              <Label>Inscrição Municipal</Label>
              <Input placeholder="Número da IM" {...field("inscricaoMunicipal")} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Razão Social *</Label>
            <Input placeholder="Nome jurídico da empresa" {...field("razaoSocial")} />
          </div>
          <div className="space-y-2">
            <Label>Nome Fantasia</Label>
            <Input placeholder="Nome do salão" {...field("nomeFantasia")} />
          </div>
          <div className="space-y-2">
            <Label>Regime Tributário</Label>
            <Select
              value={(form.regimeTributario as string) || config.regimeTributario || "simples_nacional"}
              onValueChange={(v) => { setForm((f) => ({ ...f, regimeTributario: v })); setDirty(true); }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="simples_nacional">Simples Nacional</SelectItem>
                <SelectItem value="lucro_presumido">Lucro Presumido</SelectItem>
                <SelectItem value="lucro_real">Lucro Real</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Endereço */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Endereço Fiscal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2">
              <Label>Logradouro</Label>
              <Input placeholder="Rua, Av, etc." {...field("logradouro")} />
            </div>
            <div className="space-y-2">
              <Label>Número</Label>
              <Input placeholder="123" {...field("numero")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Complemento</Label>
              <Input placeholder="Sala, Apto, etc." {...field("complemento")} />
            </div>
            <div className="space-y-2">
              <Label>Bairro</Label>
              <Input placeholder="Bairro" {...field("bairro")} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-1 space-y-2">
              <Label>CEP</Label>
              <Input placeholder="00000000" maxLength={8} {...field("cep")} />
            </div>
            <div className="col-span-1 space-y-2">
              <Label>Cidade</Label>
              <Input placeholder="Cidade" {...field("cidade")} />
            </div>
            <div className="space-y-2">
              <Label>UF</Label>
              <Input placeholder="SP" maxLength={2} {...field("uf")} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Configurações de emissão */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Configurações de Emissão</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Alíquota ISS padrão (%)</Label>
              <Input placeholder="2.00" {...field("aliquotaIss")} />
            </div>
            <div className="space-y-2">
              <Label>Código do serviço (LC 116)</Label>
              <Input placeholder="010700" maxLength={16} {...field("codigoServico")} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Descrição padrão do serviço</Label>
            <Input placeholder="Serviços de beleza e estética" {...field("descricaoServico")} />
          </div>
          <div className="flex items-center gap-3">
            <Switch
              checked={form.issRetido !== undefined ? Boolean(form.issRetido) : Boolean(config.issRetido)}
              onCheckedChange={(v) => { setForm((f) => ({ ...f, issRetido: v })); setDirty(true); }}
            />
            <Label>ISS retido pelo tomador</Label>
          </div>
          <div className="space-y-2">
            <Label>Ambiente</Label>
            <Select
              value={(form.ambiente as string) || config.ambiente || "homologacao"}
              onValueChange={(v) => { setForm((f) => ({ ...f, ambiente: v })); setDirty(true); }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="homologacao">Homologação (testes)</SelectItem>
                <SelectItem value="producao">Produção</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* API Key Notaas */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Integração Notaas</CardTitle>
          <CardDescription>
            Crie sua conta em{" "}
            <a href="https://notaas.com.br" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              notaas.com.br
            </a>{" "}
            e obtenha sua API Key no Dashboard → API Keys.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>API Key Notaas *</Label>
            <div className="flex gap-2">
              <Input
                type="password"
                placeholder={config.notaasApiKeyConfigured ? "••••••••••••••••" : "ntaas_XXXXXXXXXXXXXXXXXXXX"}
                {...field("notaasApiKey")}
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => testConn.mutate()}
                disabled={testConn.isPending || !config.notaasApiKeyConfigured}
                className="shrink-0"
              >
                {testConn.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : "Testar"}
              </Button>
            </div>
            {config.notaasApiKeyConfigured && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> API Key configurada
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end gap-3">
        <Button onClick={handleSave} disabled={save.isPending || !dirty} className="gap-2">
          {save.isPending ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Settings className="h-4 w-4" />}
          Salvar Configurações
        </Button>
      </div>
    </div>
  );
}

// ─── Aba de Notas Fiscais ─────────────────────────────────────────────────────
function NotasTab() {
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: invoices = [], refetch, isLoading } = trpc.fiscal.listar.useQuery({
    limit: 50,
    offset: 0,
    status: statusFilter !== "all" ? (statusFilter as "queued" | "processing" | "issued" | "error" | "cancelled") : undefined,
  });

  const sincronizar = trpc.fiscal.sincronizar.useMutation({
    onSuccess: (data) => {
      toast.success("Sincronizado", { description: `${data.synced} nota(s) atualizada(s).` });
      refetch();
    },
  });

  const cancelar = trpc.fiscal.cancelar.useMutation({
    onSuccess: () => {
      toast.success("Nota cancelada");
      refetch();
    },
    onError: (err) => toast.error("Erro ao cancelar", { description: err.message }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="queued">Na fila</SelectItem>
              <SelectItem value="processing">Processando</SelectItem>
              <SelectItem value="issued">Emitidas</SelectItem>
              <SelectItem value="error">Com erro</SelectItem>
              <SelectItem value="cancelled">Canceladas</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1">
            <RefreshCw className="h-3.5 w-3.5" />
            Atualizar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => sincronizar.mutate()}
            disabled={sincronizar.isPending}
            className="gap-1"
          >
            {sincronizar.isPending ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Sincronizar pendentes
          </Button>
        </div>
        <EmitirNfseDialog onSuccess={refetch} />
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Carregando notas...</div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <FileText className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="text-muted-foreground">Nenhuma nota fiscal encontrada.</p>
          <p className="text-sm text-muted-foreground">Clique em "Emitir NFS-e" para emitir sua primeira nota.</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Tomador</th>
                <th className="text-left px-4 py-3 font-medium">Descrição</th>
                <th className="text-right px-4 py-3 font-medium">Valor</th>
                <th className="text-center px-4 py-3 font-medium">Status</th>
                <th className="text-left px-4 py-3 font-medium">Nº NFS-e</th>
                <th className="text-left px-4 py-3 font-medium">Data</th>
                <th className="text-center px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="font-medium">{inv.tomadorNome}</div>
                    {inv.tomadorEmail && <div className="text-xs text-muted-foreground">{inv.tomadorEmail}</div>}
                  </td>
                  <td className="px-4 py-3 max-w-[200px]">
                    <div className="truncate text-muted-foreground">{inv.descricaoServico}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {parseFloat(inv.valorTotal).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <StatusBadge status={inv.notaasStatus} />
                    {inv.notaasStatus === "error" && inv.errorMessage && (
                      <p className="text-xs text-destructive mt-1 max-w-[120px] truncate">{inv.errorMessage}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {inv.numeroNfse ? (
                      <span className="font-mono text-xs">{inv.numeroNfse}</span>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {format(new Date(inv.createdAt), "dd/MM/yy HH:mm", { locale: ptBR })}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      {inv.pdfUrl && (
                        <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" className="h-7 w-7" title="Baixar PDF">
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                        </a>
                      )}
                      {inv.notaasStatus === "issued" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          title="Cancelar nota"
                          onClick={() => {
                            if (confirm("Confirma o cancelamento desta nota fiscal?")) {
                              cancelar.mutate({ invoiceId: inv.id });
                            }
                          }}
                        >
                          <XCircle className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function FiscalPage() {
  const [activeTab, setActiveTab] = useState("notas");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Notas Fiscais</h1>
        <p className="text-muted-foreground mt-1">
          Emita NFS-e diretamente pelo sistema via integração com a plataforma Notaas.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Mobile: caixa suspensa */}
        <div className="block md:hidden mb-2">
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Selecione a seção" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="notas">Notas Emitidas</SelectItem>
              <SelectItem value="config">Configuração Fiscal</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Desktop: abas normais */}
        <TabsList className="hidden md:inline-flex">
          <TabsTrigger value="notas" className="gap-2">
            <FileText className="h-4 w-4" />
            Notas Emitidas
          </TabsTrigger>
          <TabsTrigger value="config" className="gap-2">
            <Settings className="h-4 w-4" />
            Configuração Fiscal
          </TabsTrigger>
        </TabsList>

        <TabsContent value="notas" className="mt-6">
          <NotasTab />
        </TabsContent>

        <TabsContent value="config" className="mt-6">
          <ConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
